<template>
  <div class="bm-records">
    <div class="bm-records__tags">
      <button v-for="tag in tags" :key="tag.label" :class="{ active: activeTag === tag.label }" @click="changeTag(tag)">
        {{ tag.label }}
      </button>
      <button :disabled="loading" @click="load()">刷新</button>
    </div>
    <div class="bm-records__table-wrap">
      <table>
        <thead>
          <tr>
            <th>修改内容</th>
            <th>处理进度</th>
            <th>状态</th>
            <th>排位位置</th>
            <th>成功数</th>
            <th>失败数</th>
            <th>跳过数</th>
            <th>取消数</th>
            <th>商品总数</th>
            <th>时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="record in records" :key="record.id">
            <td>
              <strong>{{ record.type_name }}</strong>
              <small>{{ record.rule }}</small>
            </td>
            <td>
              <div class="bm-records__progress">
                <i :style="{ width: record.progress + '%' }"></i>
              </div>
              <b>{{ record.progress }}%</b>
            </td>
            <td>
              <span class="bm-records__status" :class="statusClass(record.status)">{{ record.status_name }}</span>
            </td>
            <td>
              <template v-if="record.queue_position != null && [1, 2].includes(Number(record.status))">
                第 {{ record.queue_position }} 位
                <small>前方 {{ record.queue_ahead_count }} 个待处理任务</small>
              </template>
              <template v-else>—</template>
            </td>
            <td class="success">{{ record.success_count }}</td>
            <td class="danger">{{ record.fail_count }}</td>
            <td>{{ record.skip_count }}</td>
            <td>{{ record.cancelled_count == null ? '—' : record.cancelled_count }}</td>
            <td>{{ record.total }}</td>
            <td>
              <small>
                创建 {{ record.created_at || '—' }}
                <br />
                计划 {{ record.scheduled_at || '立即执行' }}
              </small>
            </td>
            <td class="bm-records__actions">
              <button class="blue" @click="openDetail(record)">查看详情</button>
              <button
                v-if="record.cancel_supported"
                class="danger"
                :disabled="cancellingIds.includes(record.id)"
                @click="cancelRecord(record)">
                {{ cancellingIds.includes(record.id) ? '取消中…' : '取消' }}
              </button>
              <button
                v-if="record.restore_supported && ![7, 8, 9, 10, 11].includes(Number(record.type))"
                class="blue"
                :disabled="restoringIds.includes(record.id)"
                @click="restoreRecord(record)">
                {{
                  restoringIds.includes(record.id)
                    ? '恢复中…'
                    : Number(record.type) === 5
                    ? '一键恢复属性'
                    : Number(record.type) === 7
                    ? '一键恢复上下架'
                    : '一键恢复货号'
                }}
              </button>
              <small v-if="Number(record.status) === 11 && Number(record.pending_confirmation_count) > 0">
                已停止后续修改，另有 {{ record.pending_confirmation_count }} 个已提交结果待确认
              </small>
            </td>
          </tr>
          <tr v-if="!records.length && !loading">
            <td colspan="11" class="empty">暂无记录数据</td>
          </tr>
        </tbody>
      </table>
      <footer>
        <span>共 {{ pageState.total }} 条 · 共 {{ totalPages }} 页</span>
        <span>
          <button :disabled="pageState.page <= 1 || loading" @click="load(pageState.page - 1)">上一页</button>
          <b>{{ pageState.page }} / {{ totalPages }}</b>
          <button :disabled="pageState.page >= totalPages || loading" @click="load(pageState.page + 1)">下一页</button>
          <select v-model.number="pageState.limit" @change="changeLimit">
            <option :value="10">10条/页</option>
            <option :value="20">20条/页</option>
            <option :value="50">50条/页</option>
          </select>
        </span>
      </footer>
    </div>
    <div v-if="detail" class="bm-records__mask">
      <div class="bm-records__modal">
        <header>
          <strong>{{ detail.type_name }}修改详情</strong>
          <button @click="closeDetail">×</button>
        </header>
        <div class="bm-records__modal-body">
          <div v-if="Number(detail.status) === 11" class="bm-records__queue">
            已取消，停止 {{ detail.cancelled_count == null ? '—' : detail.cancelled_count }} 个商品任务
            <small v-if="Number(detail.pending_confirmation_count) > 0">
              另有 {{ detail.pending_confirmation_count }} 个已提交结果待确认，已提交到抖店的修改无法撤回
            </small>
          </div>
          <template v-if="detailLoading"><div class="empty">加载中…</div></template>
          <template v-else-if="detailUnsupported">
            <div class="empty">{{ detailUnsupported }}</div>
          </template>
          <template v-else>
            <nav>
              <button :class="{ active: detailStatus === '' }" @click="changeDetailStatus('')">全部</button>
              <button
                :class="{ active: detailStatus === 9 && (!allowsSkip || detailSkipped === '0') }"
                @click="changeDetailStatus(9, '0')">
                成功
              </button>
              <button :class="{ active: detailStatus === 10 }" @click="changeDetailStatus(10)">失败</button>
              <button
                v-if="allowsSkip"
                :class="{ active: detailStatus === 9 && detailSkipped === '1' }"
                @click="changeDetailStatus(9, '1')">
                跳过
              </button>
              <button :class="{ active: detailStatus === 8 }" @click="changeDetailStatus(8)">待确认</button>
              <button :class="{ active: detailStatus === 11 }" @click="changeDetailStatus(11)">已取消</button>
              <button :disabled="detailLoading" @click="loadDetail()">刷新</button>
            </nav>
            <table>
              <thead>
                <tr>
                  <th>商品ID</th>
                  <th>平台商品ID</th>
                  <th>修改前{{ detailColumn.label }}</th>
                  <th>修改后{{ detailColumn.label }}</th>
                  <th v-if="['stock', 'price', 'sku_code'].includes(detailSchema)">SKU结果</th>
                  <th>状态</th>
                  <th class="bm-records__error-column">失败原因</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in detailRows" :key="item.id">
                  <td>{{ item.distribute_product_id }}</td>
                  <td>{{ item.platform_product_id || '—' }}</td>
                  <td :class="{ 'purchase-limit-value': detailSchema === 'purchase_limit' }">
                    {{ detailValue(item, false) }}
                    <doudian-size-chart-preview v-if="detailSchema === 'size_chart'" :chart="item.old_values && item.old_values.chart" />
                    <small v-if="detailSchema === 'size_chart'">目标店铺：{{ item.shop_id }}</small>
                    <el-image v-for="url in crossFabricImages(item, false)" :key="url" style="width:48px;height:48px" :src="url" :preview-src-list="crossFabricImages(item, false)" fit="cover" />
                    <doudian-video-preview v-if="detailSchema === 'main_video' && item.old_video && item.old_video.known && item.old_video.product_video_id" :available="true" :request-data="{ batch_id: Number(detail.id), detail_id: Number(item.id), after: false }" label="播放原视频" />
                    <template v-if="detailSchema === 'fabric'">
                      <el-image
                        v-for="url in fabricImages(item, false)"
                        :key="url"
                        style="width: 64px; height: 64px"
                        :src="url"
                        :preview-src-list="fabricImages(item, false)"
                        fit="cover" />
                    </template>
                    <template v-if="detailSchema === 'main_image'">
                      <el-image v-for="url in mainImageUrls(item, false)" :key="url" style="width:64px;height:64px" :src="url" :preview-src-list="mainImageUrls(item, false)" fit="cover" />
                    </template>
                  </td>
                  <td :class="{ 'purchase-limit-value': detailSchema === 'purchase_limit' }">
                    {{ detailValue(item, true) }}
                    <doudian-size-chart-preview v-if="detailSchema === 'size_chart' && !item.is_skipped" :chart="item.target_values && item.target_values.chart" />
                    <small v-if="detailSchema === 'size_chart' && item.target_values && item.target_values.source_shop_id">来源店铺：{{ item.target_values.source_shop_id }} / {{ item.target_values.source_template_id }}</small>
                    <el-image
                      v-for="url in crossFabricImages(item, true)"
                      :key="url"
                      style="width: 48px; height: 48px"
                      :src="url"
                      :preview-src-list="crossFabricImages(item, true)"
                      fit="cover" />
                    <template v-if="detailSchema === 'main_video'">
                      <doudian-video-preview v-if="item.target_video && item.target_video.action === 'set' && (item.target_video.product_video_id || item.source_id)" :available="true" :request-data="item.target_video.product_video_id ? { batch_id: Number(detail.id), detail_id: Number(item.id), after: true } : { source_id: Number(item.source_id) }" :label="item.target_video.name || '播放目标视频'" />
                      <small v-if="item.material_status">{{ videoMaterialStatus(item.material_status) }}</small>
                    </template>
                    <template v-if="detailSchema === 'fabric'">
                      <el-image
                        v-for="url in fabricImages(item, true)"
                        :key="url"
                        style="width: 64px; height: 64px"
                        :src="url"
                        :preview-src-list="fabricImages(item, true)"
                        fit="cover" />
                      <small v-if="item.attribute_reasons">
                        {{
                          { pending: '素材审核中', approved: '素材审核通过', failed: '素材审核失败' }[
                            item.attribute_reasons.audit_status
                          ] || ''
                        }}
                      </small>
                    </template>
                    <template v-if="detailSchema === 'main_image'">
                      <el-image v-for="url in mainImageUrls(item, true)" :key="url" style="width:64px;height:64px" :src="url" :preview-src-list="mainImageUrls(item, true)" fit="cover" />
                      <div v-for="material in item.image_materials || []" :key="material.id" class="main-image-material-state">
                        <el-image v-if="material.preview_url" :src="material.preview_url" :preview-src-list="[material.preview_url]" style="width:48px;height:48px" fit="cover" />
                        <small>{{ mainImageMaterialState(material) }}<span v-if="material.reject_reason || material.last_error_message">：{{ material.reject_reason || material.last_error_message }}</span></small>
                        <button v-if="material.material_id" class="blue" :disabled="material.refreshing || mainImageRefreshWait(material) > 0" @click="refreshMainImageMaterial(material)">{{ material.refreshing ? '查询中…' : mainImageRefreshWait(material) ? `${mainImageRefreshWait(material)}秒后刷新` : '刷新审核' }}</button>
                      </div>
                    </template>
                  </td>
                  <td v-if="['stock', 'price', 'sku_code'].includes(detailSchema)"><button class="blue" @click="openStockSkus(item)">查看 SKU</button></td>
                  <td>
                    {{
                      item.is_skipped
                        ? '已跳过'
                        : detailSchema === 'main_video'
                        ? videoStatusName(item)
                        : ['purchase_limit', 'freight', 'size_chart', 'stock_count', 'shipping_mode', 'seven_day_return'].includes(detailSchema) && item.outcome === 'result_unknown'
                        ? '结果未知'
                        : ['purchase_limit', 'freight', 'size_chart', 'stock_count', 'shipping_mode', 'seven_day_return'].includes(detailSchema) && Number(item.status) === 9
                        ? item.confirmed_at && item.confirmed_values ? '已确认' : item.accepted_at ? '平台返回成功' : '待核查'
                        : allowsSkip && Number(item.status) === 9
                        ? '已受理'
                        : detailStatusName(item.status)
                    }}
                  </td>
                  <td class="danger bm-records__error-column">
                    <span class="bm-records__error-text" :title="detailReason(item)">
                      {{ detailReason(item) || '—' }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!detailRows.length">
                  <td :colspan="['stock', 'price', 'sku_code'].includes(detailSchema) ? 7 : 6" class="empty">暂无明细</td>
                </tr>
              </tbody>
            </table>
            <footer>
              <span>共 {{ detailPage.total }} 条 · 共 {{ detailTotalPages }} 页</span>
              <el-pagination
                class="bm-records__detail-pagination"
                small
                layout="prev, pager, next, jumper"
                :pager-count="5"
                :current-page="detailPage.page"
                :page-size="detailPage.limit"
                :total="detailPage.total"
                :disabled="detailLoading"
                @current-change="loadDetail" />
              <button @click="closeDetail">关闭</button>
            </footer>
          </template>
        </div>
      </div>
    </div>
    <div v-if="stockSkuVisible" class="bm-records__mask">
      <div class="bm-records__modal">
        <header><strong>SKU {{ detailSchema === 'sku_code' ? '编码' : (detailSchema === 'price' ? '价格' : '库存') }}修改结果</strong><button @click="closeStockSkus">×</button></header>
        <div class="bm-records__modal-body">
          <table><thead><tr><th>SKU ID</th><th>规格</th><th>修改前</th><th>修改后</th><th>结果</th><th>原因</th></tr></thead>
            <tbody><tr v-for="sku in stockSkuRows" :key="sku.sku_id"><td>{{ sku.sku_id }}</td><td>{{ sku.sku_spec_text || '待核验' }}</td><td>{{ detailSchema === 'sku_code' ? (sku.value_ready ? (sku.old_code === '' ? '（空）' : sku.old_code) : '未知') : detailSchema === 'price' ? (sku.old_price == null ? '未知' : '¥' + sku.old_price) : (sku.old_stock == null ? '未知' : sku.old_stock) }}</td><td>{{ detailSchema === 'sku_code' ? (sku.new_value_ready ? (sku.new_code === '' ? '（空）' : sku.new_code) + (sku.is_random_example ? '（随机示例）' : '') : '—') : detailSchema === 'price' ? (sku.new_price == null ? '—' : '¥' + sku.new_price) : (sku.new_stock == null ? '—' : sku.new_stock) }}</td><td>{{ ['sku_code','price'].includes(detailSchema) ? priceSkuResult(sku.result) : sku.result }}</td><td>{{ sku.reason || '—' }}</td></tr><tr v-if="!stockSkuRows.length"><td colspan="6" class="empty">暂无 SKU 结果</td></tr></tbody>
          </table>
          <footer><el-pagination small layout="prev, pager, next" :current-page="stockSkuPage.page" :page-size="stockSkuPage.limit" :total="stockSkuPage.total" @current-change="loadStockSkus" /><button @click="closeStockSkus">关闭</button></footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DoudianSizeChartPreview from '@/components/DoudianSizeChartPreview.vue'

import DoudianVideoPreview from '@/components/DoudianVideoPreview.vue'
import {
  getDoudianBatchDetail,
  getDoudianBatchList,
  cancelDoudianBatch,
  restoreDoudianAttributeBatch,
  restoreDoudianHuohaoBatch,
  restoreDoudianShelfBatch,
  getDoudianStockDetailSkus,
  getDoudianPriceDetailSkus,
  getDoudianSkuCodeDetailSkus,
  getDoudianSevenDayReturnPolicies,
  refreshDoudianMainImageMaterial
} from '@/api/main/doudianBatchEdit'

const typeMap = { 标题: 1, 导购短标题: 2, 品牌: 3, 货号: 4, 商品属性: 5, 面料材质: 6, 上下架: 7, 定时上下架: 8, 删除商品: 9, 库存: 10, '价格(SKU)': 11, 主图: 12, 商品限购: 13, 主图视频: 14, 运费模板: 15, SKU编码: 16, 尺码表: 17, 库存计数: 18, '发货模式(SKU时效)': 19, '7天无理由退换货': 20 }
// 抖店标准品牌库中"无品牌"的固定品牌 ID。
const NO_BRAND_ID = '596120136'
// 明细列按后端 detail_schema 渲染；新增业务类型在此登记列名、取值方法与是否支持跳过。
const detailColumns = {
  size_chart: { label: '尺码表', value: 'sizeChartValue', allowsSkip: true },
  shipping_mode: { label: '发货模式', value: 'shippingModeValue', allowsSkip: true },
  seven_day_return: { label: '退货政策', value: 'sevenDayReturnValue', allowsSkip: true },
  stock_count: { label: '库存计数', value: 'stockCountValue', allowsSkip: true },
  freight: { label: '运费模板', value: 'freightValue', allowsSkip: true },
  main_video: { label: '主图视频', value: 'mainVideoValue', allowsSkip: true },
  purchase_limit: { label: '商品限购', value: 'purchaseLimitValue', allowsSkip: true },
  title: { label: '标题', value: 'titleValue', allowsSkip: false },
  short_title: { label: '短标题', value: 'shortTitleValue', allowsSkip: true },
  brand: { label: '品牌', value: 'brandValue', allowsSkip: true },
  attribute: { label: '属性', value: 'attributeValue', allowsSkip: true },
  fabric: { label: '面料/图片', value: 'fabricValue', allowsSkip: true },
  huohao: { label: '货号', value: 'huohaoValue', allowsSkip: true },
  shelf: { label: '上下架', value: 'shelfValue', allowsSkip: true },
  product_delete: { label: '删除状态', value: 'productDeleteValue', allowsSkip: true },
  stock: { label: '库存 SKU', value: 'stockValue', allowsSkip: true },
  sku_code: { label: 'SKU编码', value: 'stockValue', allowsSkip: true },
  price: { label: '价格 SKU', value: 'stockValue', allowsSkip: true },
  main_image: { label: '主图', value: 'mainImageValue', allowsSkip: true }
}
const tags = [
  '全部',
  '标题',
  '导购短标题',
  '品牌',
  '商品属性',
  '跨类目属性',
  '面料材质',
  '货号',
  '删除商品',
  '下架/删除低效品',
  '上下架',
  '定时上下架',
  '创建渠道品',
  '限时限量购',
  '食品安全/营养成分表',
  '价格(SKU)',
  '库存',
  'SKU编码',
  'SKU规格名称/备注',
  '添加SKU',
  '删除SKU',
  '发货模式(SKU时效)',
  '预售时间',
  '商品详情图',
  '主图',
  '主图3:4',
  '库存计件',
  '尺码表',
  '库存计数',
  '区域库存开关',
  'SKU状态',
  'SKU规格图',
  'SKU图',
  '替换商品图片',
  '统一设置SKU',
  '白底图',
  '导入修改(多字段)',
  'SKU条形码（国补码）',
  '引用SKU模板',
  '多地址退货策略',
  '精选联盟策略',
  '主图视频',
  '运费模板',
  '商品限购',
  '服务资质',
  '7天无理由退换货',
  '大件商品',
  '三包服务承诺',
  '虚拟商品',
  '安装模式',
  '综合修改(多字段修改)',
  '价格-库存-SKU编码'
].map((label) => ({
  label,
  type: label === '跨类目属性' ? 5 : label === '全部' ? '' : typeMap[label] || 0
}))
const statusNames = {
  3: '待同步详情',
  4: '处理中',
  5: '图片上传中',
  6: '图片审核中',
  7: '待提交修改',
  8: '待确认平台结果',
  9: '已完成',
  10: '失败',
  11: '已取消'
}

export default {
  components: { DoudianSizeChartPreview, DoudianVideoPreview },
  name: 'BatchModifyRecords',
  data() {
    return {
      tags,
      activeTag: '全部',
      activeType: '',
      records: [],
      loading: false,
      queueSummary: {},
      cancellingIds: [],
      restoringIds: [],
      listRequestId: 0,
      detailRequestId: 0,
      pageState: { page: 1, limit: 10, total: 0 },
      detail: null,
      detailRows: [],
      sevenDayReturnPolicyNames: {},
      detailStatus: '',
      detailSkipped: '',
      detailSchema: '',
      materialClock: Date.now(),
      materialClockTimer: null,
      refreshTimer: null,
      refreshing: false,
      detailPage: { page: 1, limit: 20, total: 0 },
      detailLoading: false,
      detailUnsupported: '',
      stockSkuVisible: false,
      stockSkuRows: [],
      stockSkuDetailId: 0,
      stockSkuRequestId: 0,
      stockSkuPage: { page: 1, limit: 20, total: 0 }
    }
  },
  computed: {
    detailColumn() {
      return detailColumns[this.detailSchema || this.detail?.detail_schema] || detailColumns.title
    },
    allowsSkip() {
      return this.detailColumn.allowsSkip
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.pageState.total / this.pageState.limit))
    },
    detailTotalPages() {
      return Math.max(1, Math.ceil(this.detailPage.total / this.detailPage.limit))
    }
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer)
    clearInterval(this.materialClockTimer)
    this.listRequestId++
    this.detailRequestId++
  },
  created() {
    this.materialClockTimer = setInterval(() => { this.materialClock = Date.now() }, 1000)
    this.load()
  },
  methods: {
    sizeChartValue(item, after) {
      if (after && item.is_skipped) return '未修改'
      const values = after ? item.target_values : item.old_values
      if (after && item.target_actions?.action === 'remove') {
        if (item.outcome === 'failed') return '删除失败，未确认修改'
        return item.confirmed_at ? '已回读确认：尺码表已删除' : item.accepted_at ? '删除已受理，等待回读确认' : '计划删除尺码表'
      }
      if (!values || values.size_info_template_id == null) return after ? '执行时生成或复制' : '待实时核验'
      return (after ? item.confirmed_at ? '已回读确认：' : item.accepted_at ? '已受理待确认：' : '计划：' : '原模板：') + values.size_info_template_id
    },
    shippingModeValue(item, after) {
      if (after && item.is_skipped) return '未修改'
      const values = after ? item.confirmed_values || item.target_values : item.old_values
      if (!values || values.presell_type == null) return '待核验'
      let text =
        Number(values.presell_type) === 0
          ? '现货：' + ({ 9999: '当日发', 1: '次日发', 2: '48小时发' }[values.delivery_delay_day] || '待核验')
          : Number(values.presell_type) === 1
            ? '预售：' +
              (values.presell_end_time || '') +
              '，' +
              (values.presell_delay == null ? '时效待核验' : values.presell_delay + '天')
            : '阶梯/特殊模式'
      if (Number(values.presell_type) === 2 && Array.isArray(values.spec_prices_v2)) {
        const skus = values.spec_prices_v2
        const pools = new Map()
        let spot = 0
        let complete = true
        for (const sku of skus) {
          if (sku.stock_num == null || !Array.isArray(sku.multi_time_stocks)) {
            complete = false
            break
          }
          spot += Number(sku.stock_num)
          for (const pool of sku.multi_time_stocks) {
            if (pool.stock_num == null) complete = false
            pools.set(pool.time_type, (pools.get(pool.time_type) || 0) + Number(pool.stock_num))
          }
        }
        text =
          after && item.target_actions?.operation === 'set_presale' && item.target_actions?.end_mode === 'none'
            ? '预售（无截止，支付后' + item.target_actions.delay_days + '天）'
            : '现货预售混合'
        text +=
          '：' +
          skus.length +
          '个SKU；' +
          (complete
            ? '现货' +
              spot +
              '件；' +
              [...pools]
                .sort((a, b) => Number(a[0]) - Number(b[0]))
                .map(([day, stock]) => day + '天档' + stock + '件')
                .join('；')
            : '库存待实时核验')
      }
      const delay = values.delay_rule
      const holidayTime = (value) =>
        Number.isFinite(Number(value)) && Number(value) > 0
          ? new Date(Number(value) * 1000 + 8 * 3600000).toISOString().slice(0, 19).replace('T', ' ')
          : '待核验'
      const holiday = delay
        ? delay.enable === false
          ? '；特殊日期：关闭'
          : delay.enable === true
            ? `；特殊日期：下单${holidayTime(delay.start_time)}至${holidayTime(delay.end_time)}，${Number(delay.config_type) === 2 ? `支付后${delay.config_value}天发货` : `最晚${holidayTime(delay.config_value)}发货`}`
            : '；特殊日期：待核验'
        : after && item.target_actions?.holiday_action === 'keep'
          ? '；特殊日期：保持不变'
          : ''
      return (
        (after ? (item.confirmed_at ? '已回读确认：' : item.accepted_at ? '平台已受理：' : '计划：') : '') +
        text +
        holiday
      )
    },
    sevenDayReturnValue(item, after) {
      if (after && item.is_skipped) return '未修改'
      const values = after ? item.confirmed_values || item.target_values : item.old_values
      const value = values && values.selector
      const options = item.target_values?.rule?.options || []
      const option = options.find(x => x.value === value)
      const name = option?.name || this.sevenDayReturnPolicyNames[value]
      return (
        (name || (value ? `未收录政策（${value}）` : '待核验')) +
        (after && item.target_values?.matched_priority ? `（优先级${item.target_values.matched_priority}）` : '')
      )
    },
    async loadSevenDayReturnPolicyNames(batchId, requestId) {
      try {
        const data = this.responseData(await getDoudianSevenDayReturnPolicies())
        if (requestId !== this.detailRequestId || this.detail?.id !== batchId || this._isDestroyed) return
        this.sevenDayReturnPolicyNames = Object.fromEntries(
          (data.options || []).map(option => [option.value, option.label])
        )
      } catch (error) {
        if (requestId === this.detailRequestId && this.detail?.id === batchId && !this._isDestroyed)
          this.$message.error('退货政策名称加载失败，请刷新重试')
      }
    },
    stockCountValue(item, after) {
      if (after && item.is_skipped) return '未修改'
      const values = after ? item.confirmed_values || item.target_values : item.old_values
      const value = values && values.reduce_type
      const label = value === 1 ? '拍下减库存' : value === 2 ? '付款减库存' : '待核验'
      return (after ? item.confirmed_at ? '已回读确认：' : item.accepted_at ? '已受理待审核：' : '计划：' : '') + label
    },
    freightValue(item, after) {
      if (after && item.is_skipped) return '未修改'
      const values = after ? item.confirmed_values || item.target_values : item.old_values
      if (values?.freight_id == null) return '待核验'
      const prefix = after && !item.confirmed_at ? item.accepted_at ? '平台返回成功：' : '计划：' : ''
      return prefix + (values.template_name ? values.template_name + '（' + values.freight_id + '）' : '模板ID：' + values.freight_id)
    },
    mainVideoValue(item, after) {
      if (after && item.is_skipped) return '未修改'
      const video = after ? item.confirmed_video || item.target_video : item.old_video
      if (!video || (!after && !video.known)) return '视频信息待核验'
      if (after && video.action === 'delete') return item.confirmed_at ? '已删除商品主图视频' : '计划删除商品主图视频'
      if (video.product_video_id === '') return '无视频'
      return video.product_video_id || (after ? '目标视频等待审核' : '视频信息待核验')
    },
    videoStatusName(item) {
      if (item.outcome === 'result_unknown') return '结果未知，需核查'
      if (Number(item.status) === 9) return item.confirmed_at ? '已确认' : '待核查'
      return { 3: '待查询详情', 4: '视频上传中', 5: '视频审核中', 7: '待提交视频修改', 8: '待查询确认', 10: '失败', 11: '已取消' }[Number(item.status)] || '处理中'
    },
    videoMaterialStatus(status) {
      return { pending: '待上传', uploading: '视频上传中', auditing: '视频审核中', ready: '视频审核通过', rejected: '视频审核拒绝', failed: '视频素材失败', upload_unknown: '上传结果未知，需核查', audit_timeout: '审核超时，需核查' }[status] || status
    },
    purchaseLimitValue(item, after) {
      const fields = [['limit_per_buyer', '累计'], ['maximum_per_order', '每次'], ['minimum_per_order', '起购']]
      const completed = Number(item.status) === 9 && !item.is_skipped
      const confirmed = completed && item.confirmed_at && item.confirmed_values
      const accepted = completed && item.accepted_at
      if (after && item.is_skipped) return '未修改'
      const values = after ? (confirmed ? item.confirmed_values : item.target_values) : item.old_values
      const label = (value, key) => value == null ? '待核验' : value === 0 ? (key === 'maximum_per_order' ? '未设置（按类目推荐）' : '未设置（平台值0）') : `${value} 件`
      const lines = fields.map(([key, name]) => {
        const action = after ? item.target_actions?.[key] : 'keep'
        const text = after && action === 'clear' ? (key === 'minimum_per_order' ? '恢复1件起购' : key === 'maximum_per_order' ? '清空（按类目推荐）' : '清空') : label(values?.[key], key)
        return `${name}：${text}${confirmed && action === 'clear' ? `（已确认${values[key]}）` : after && action === 'keep' ? '（不修改）' : ''}`
      })
      return `${after && !confirmed ? accepted ? '平台返回成功，目标值：\n' : '计划（未确认生效）：\n' : ''}${lines.join('\n')}`
    },
    scheduleRefresh() {
      clearTimeout(this.refreshTimer)
      if (this._isDestroyed) return
      const pending = (item) =>
        item && (![9, 10, 11].includes(Number(item.status)) || Number(item.pending_confirmation_count) > 0)
      if (!this.records.some(pending) && !pending(this.detail) && !(Number(this.queueSummary.waiting_count) > 0)) return
      this.refreshTimer = setTimeout(async () => {
        if (!this.loading && !this.detailLoading && !this.refreshing) {
          this.refreshing = true
          try {
            await this.load(this.pageState.page, true)
            if (this.detail) await this.loadDetail(this.detailPage.page, true)
          } finally {
            this.refreshing = false
          }
        }
        this.scheduleRefresh()
      }, 10000)
    },
    shortTitleValue(item, after) {
      if (!item.value_ready) return after && item.operation === 'clear' ? '计划清空（待计算）' : '待同步详情'
      if (after && item.operation === 'clear')
        return Number(item.status) === 9 && !item.is_skipped ? '已受理清空' : '计划清空'
      const value = after ? item.new_short_title : item.old_short_title
      return value === null || value === undefined || value === '' ? '未设置' : value
    },
    brandValue(item, after) {
      const id = after ? item.new_brand_id : item.old_brand_id
      if (String(id) === NO_BRAND_ID) return '无品牌'
      return (after ? item.new_brand_name : item.old_brand_name) || '品牌名称待同步'
    },
    crossFabricImages(item, after) {
      if (this.detailSchema !== 'attribute') return []
      const fabric = item.attribute_reasons?.cross_fabric
      if (!fabric) return []
      const urls = after
        ? item.attribute_reasons?.fabric_audit?.final_url
          ? [item.attribute_reasons.fabric_audit.final_url]
          : fabric.new_images
        : fabric.old_images
      return Array.isArray(urls) ? urls.filter((url) => typeof url === 'string' && /^https:\/\//.test(url)) : []
    },
    attributeValue(item, after) {
      if (item.common_only) return '属性未修改，仅处理上下架/尺码表'
      const fabric = item.attribute_reasons?.cross_fabric
      if (fabric) {
        const values = after ? item.new_property_json : item.old_property_json
        const attrs = Object.keys(values || {})
          .map(
            (id) =>
              ((item.attribute_names || {})[id] || id) +
              '：' +
              values[id].rows.map((row) => row.name || String(row.value ?? '')).join('、')
          )
          .join('；')
        const audit = item.attribute_reasons?.fabric_audit?.status
        return [
          attrs,
          '水洗标/吊牌图：' + (after ? fabric.new_images : fabric.old_images).length + '张',
          after ? { pending: '素材审核中', approved: '素材审核通过', failed: '素材审核失败' }[audit] || '' : ''
        ]
          .filter(Boolean)
          .join('；')
      }
      const values = after ? item.new_property_json : item.old_property_json
      if (!values || !Object.keys(values).length) return '无修改项或待同步详情'
      return Object.keys(values)
        .map(
          (id) =>
            ((item.attribute_names || {})[id] || id) +
            '：' +
            (values[id].present
              ? values[id].rows
                  .map((row) => row.name || row.Name || String(row.value === undefined ? row.Value || '' : row.value))
                  .join('、')
              : after
              ? '清空属性内容'
              : '未设置')
        )
        .join('；')
    },
    shelfValue(item, after) {
      if (!after) return item.old_platform_status_name || '待同步'
      const time = [item.online_at ? '上架 ' + item.online_at : '', item.offline_at ? '下架 ' + item.offline_at : '']
        .filter(Boolean)
        .join('；')
      return [item.action_name, item.result_platform_status_name, time, item.virtual_offline ? '虚拟下架' : '']
        .filter(Boolean)
        .join(' ')
    },
    productDeleteValue(item, after) {
      if (!after) return item.old_platform_status_name || '待同步详情'
      if (item.is_skipped) return '已跳过，未删除'
      if (item.outcome === 'result_unknown') return '结果未知，需人工核查'
      if (Number(item.status) === 9) return '已删除'
      return Number(item.status) === 10 ? '删除失败' : Number(item.status) === 11 ? '已取消' : '待执行或确认'
    },
    priceSkuResult(value) { return { success: '成功', failed: '失败', skipped: '跳过', pending: '待处理', cancelled: '已取消', unknown: '待确认' }[value] || value },
    stockValue(item, after) {
      if (!item.sku_total_count) return 'SKU 明细待加载'
      return after
        ? `变化 ${item.sku_changed_count || 0}，成功 ${item.sku_success_count || 0}，失败 ${item.sku_fail_count || 0}，跳过 ${item.sku_skip_count || 0}`
        : `共 ${item.sku_total_count} 个 SKU`
    },
    huohaoValue(item, after) {
      if (item.skip_reason === 'property_not_supported') return after ? '不适用' : '类目无货号属性'
      if (item.value_ready === false) return '待同步详情'
      const value = after ? item.new_huohao : item.old_huohao
      return value === null || value === undefined || value === '' ? '未设置' : value
    },
    titleValue(item, after) {
      return this.detailTitle(after ? item.new_title : item.old_title, item.status)
    },
    detailValue(item, after) {
      return this[this.detailColumn.value](item, after)
    },
    fabricValue(item, after) {
      const value = after ? item.new_property_json : item.old_property_json
      const rows = value && (after ? value.new_composition : value.composition)
      return Array.isArray(rows) && rows.length
        ? rows.map((row) => row.name + ' ' + row.percent + '%').join('、')
        : '未记录'
    },
    fabricImages(item, after) {
      const value = after ? item.new_property_json : item.old_property_json
      const urls = value && (after ? value.new_images : value.images)
      return Array.isArray(urls) ? urls.filter((url) => /^https:/.test(url)) : []
    },
    mainImageMaterialState(material) {
      const uploads = { waiting_source: '等待图片暂存', queued: '等待上传抖店', uploading: '正在上传抖店', unknown: '上传结果待核查', failed: '图片上传失败' }
      return uploads[material.upload_status] || ({ approved: '抖店审核通过', pending: '抖店审核中', rejected: '抖店审核驳回', unknown: '审核状态待确认' })[material.status] || '处理中'
    },
    mainImageRefreshWait(material) { return Math.min(10, Math.max(0, Math.ceil(((material.nextRefreshAt || 0) - this.materialClock) / 1000))) },
    async refreshMainImageMaterial(material) {
      if (!material.material_id || material.refreshing || this.mainImageRefreshWait(material)) return
      this.$set(material, 'refreshing', true)
      try {
        const data = this.responseData(await refreshDoudianMainImageMaterial(material.id))
        if (this._isDestroyed || !this.detailRows.some(row => (row.image_materials || []).includes(material))) return
        this.materialClock = Date.now()
        Object.assign(material, data, { nextRefreshAt: this.materialClock + Number(data.retry_after_seconds || 0) * 1000 })
      } catch (error) {
        this.materialClock = Date.now()
        this.$set(material, 'nextRefreshAt', this.materialClock + Number(error.response?.data?.data?.retry_after_seconds || 10) * 1000)
        this.$message.warning(error.response?.data?.message || error.message || '审核查询失败')
      } finally { material.refreshing = false }
    },
    mainImageValue(item, after) {
      if (after && [5, 6].includes(Number(item.status))) return '等待素材上传及审核'
      return this.mainImageUrls(item, after).length + ' 张图片'
    },
    mainImageUrls(item, after) {
      const value = after ? item.new_images_json : item.old_images_json
      return Array.isArray(value) ? value.filter((url) => /^https:/.test(url)) : []
    },
    detailReason(item) {
      if (this.detailSchema === 'attribute' && item.attribute_mode === 'cross_category') {
        const results = item.rule_results || []
        const partial =
          Number(item.status) === 9 &&
          !item.is_skipped &&
          !item.common_only &&
          results.some((result) => result.result === 'ready') &&
          results.some((result) => result.result !== 'ready')
        return (
          (partial ? '已修改部分属性；' : '') +
          (item.error_msg ||
            results.map((result) => result.name + '：' + result.message).join('；') ||
            item.skip_reason ||
            '')
        )
      }
      if (this.detailSchema === 'size_chart') return item.error_msg || ({ valid_chart: '尺码表正常，无需修改', not_required: '类目非必填，无需修复', not_supported: '类目不支持尺码表', existing_chart: '已有尺码表，增加动作跳过', missing_chart: '无尺码表，按规则跳过', unmapped_category: '未配置该商品类目尺码表', no_change: '尺码表内容相同', condition_not_matched: '未命中筛选条件', user_excluded: '用户已排除' }[item.skip_reason]) || item.skip_reason || ''
      if (this.detailSchema === 'shipping_mode') return item.error_msg || ({ platform_product_deleted: '平台商品已删除，已跳过', presale_not_supported: '该店铺当前类目不支持所选预售方式，已跳过', no_shipping_change: '发货设置和公共动作均无变化', source_unknown: '原数据待核验', source_conversion_unsupported: '来源转换暂不支持', special_inventory: '特殊库存暂不支持', protocol_unverified: '该方式仅支持预览' }[item.skip_reason]) || item.skip_reason || ''
      if (this.detailSchema === 'seven_day_return') return item.error_msg || ({ no_policy_change: '已使用目标政策', no_matching_policy: '全部优先级均不支持', policy_disabled: '类目未开放此政策' }[item.skip_reason]) || ''
      if (this.detailSchema === 'stock_count') return item.error_msg || ({ no_reduce_type_change: '商品已使用目标方式', reduce_type_unknown: '原方式待核验', condition_unknown: '条件缺少核验数据', condition_not_matched: '未命中筛选条件' }[item.skip_reason]) || ''
      if (this.detailSchema === 'freight') return item.error_msg || ({ no_freight_change: '商品已使用目标模板', freight_unknown: '原模板数据待核验', condition_unknown: '条件缺少核验数据', condition_not_matched: '未命中筛选条件' }[item.skip_reason]) || ''
      if (this.detailSchema === 'purchase_limit') {
        return item.error_msg || ({ condition_not_matched: '未命中商品条件', condition_unknown: '商品条件缺少核验数据', value_unknown: '限购原值缺失或格式无效', no_change: '限购设置未变化', limit_conflict: '起购数、每次上限或累计上限存在冲突', user_excluded: '用户已排除' }[item.skip_reason]) || ''
      }
      return (
        item.error_msg ||
        (this.detailSchema === 'fabric' ? '' : Object.values(item.attribute_reasons || {}).join('；')) ||
        item.match_reason ||
        item.brand_eligibility_reason ||
        {
          property_not_supported: '类目不支持货号属性，已过滤',
          restore_expired: '已超过2天恢复期限，已跳过',
          already_restored: '已恢复为原值，已跳过',
          restore_conflict: '商品属性或类目已变化，为避免覆盖已跳过',
          category_not_editable: '本地类目定义不可用，已跳过',
          property_requires_image: '待恢复属性现已要求图片，只能保持原值，已跳过',
          category_changed: '商品类目已变化，已跳过',
          all_attributes_unchanged: '属性无需修改且未选择上下架或自动尺码表，已跳过',
          no_change: '修改前后内容一致，已跳过',
          no_price_change: '价格未变化且无其他修改，已跳过',
          sku_not_matched: '未命中SKU条件，已跳过',
          sku_identity_unknown: 'SKU标识缺失或重复，已跳过',
          sku_price_or_type_unknown: 'SKU价格或类型无法确认，已跳过',
          sku_name_unknown: 'SKU规格名称缺失，已跳过',
          price_unknown: 'SKU价格缺失，已跳过',
          unsupported_price_type: '该SKU类型暂不支持价格修改',
          PRICE_GUARD_MIN_PERCENT: '新价格低于原价保护比例，整件跳过',
          PRICE_GUARD_MIN: '新价格低于保护下限，整件跳过',
          PRICE_GUARD_MAX: '新价格高于保护上限，整件跳过',
          price_out_of_range: '计算价格超出有效范围，整件跳过',
          condition_not_matched: '未命中修改条件，已跳过',
          onsale_protected: '在售商品不删除，已跳过',
          already_deleted: '商品已删除，本次未重复删除'
        }[item.skip_reason] ||
        ''
      )
    },
    responseData(response) {
      const root = response && response.code !== undefined ? response : response?.data
      if (Number(root?.code) !== 200) throw new Error(root?.message || root?.msg || '请求失败')
      return root.data || {}
    },
    async cancelRecord(record) {
      if (this.cancellingIds.includes(record.id)) return
      this.cancellingIds.push(record.id)
      try {
        await this.$confirm('取消后将停止后续修改，已提交到抖店的修改无法撤回，是否继续？', '取消批量修改', {
          confirmButtonText: '确认取消',
          cancelButtonText: '继续执行',
          type: 'warning',
          customClass: 'bm-records-cancel-confirm'
        })
      } catch (_) {
        this.cancellingIds = this.cancellingIds.filter((id) => id !== record.id)
        return
      }
      if (this._isDestroyed) return
      let responseReceived = false
      try {
        const response = await cancelDoudianBatch(record.id)
        responseReceived = true
        const data = this.responseData(response)
        if (this._isDestroyed) return
        this.listRequestId++
        this.detailRequestId++
        this.detailLoading = false
        const patch = { ...data, cancel_supported: false, queue_ahead_count: null, queue_position: null }
        this.records = this.records.map((item) => (item.id === record.id ? { ...item, ...patch } : item))
        if (this.detail?.id === record.id) this.detail = { ...this.detail, ...patch }
        this.$message.success('任务已取消')
        await this.load(this.pageState.page, false, '取消已成功，记录刷新失败，请稍后刷新')
        if (this.detail?.id === record.id) await this.loadDetail(this.detailPage.page)
      } catch (error) {
        if (!this._isDestroyed) {
          this.$message.error(
            !responseReceived && !error?.response
              ? '取消结果待核实，请刷新记录'
              : error?.response?.data?.message || error?.message || '取消失败，请刷新记录'
          )
          await this.load(this.pageState.page, true)
        }
      } finally {
        this.cancellingIds = this.cancellingIds.filter((id) => id !== record.id)
        this.scheduleRefresh()
      }
    },
    async restoreRecord(record) {
      if (this.restoringIds.includes(record.id)) return
      this.restoringIds.push(record.id)
      try {
        const type = Number(record.type)
        const label = type === 5 ? '属性' : type === 7 ? '上下架' : '货号'
        await this.$confirm('将按原批次成功修改的' + label + '创建恢复任务，是否继续？', '一键恢复' + label, {
          confirmButtonText: '创建恢复任务',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const request = type === 5 ? restoreDoudianAttributeBatch : type === 7 ? restoreDoudianShelfBatch : restoreDoudianHuohaoBatch
        const data = this.responseData(await request(record.id))
        this.$message.success('恢复任务已创建：' + data.batch_no)
        await this.load(this.pageState.page)
      } catch (error) {
        if (error !== 'cancel' && error !== 'close')
          this.$message.error(error?.response?.data?.message || error?.message || '恢复任务创建失败')
      } finally {
        this.restoringIds = this.restoringIds.filter((id) => id !== record.id)
      }
    },
    async load(page = this.pageState.page, background = false, failureMessage = '') {
      const requestId = ++this.listRequestId
      this.loading = true
      try {
        const params = { page, limit: this.pageState.limit }
        if (this.activeType !== '') params.type = this.activeType
        if (this.activeTag === '跨类目属性' || this.activeTag === '商品属性')
          params.attribute_mode = this.activeTag === '跨类目属性' ? 'cross_category' : 'category'
        const data = this.responseData(await getDoudianBatchList(params))
        if (requestId !== this.listRequestId || this._isDestroyed) return
        this.records = Array.isArray(data.list) ? data.list : []
        this.queueSummary = data.queue_summary || {}
        if (this.detail) {
          const current = this.records.find((item) => item.id === this.detail.id)
          if (current) this.detail = current
        }
        this.pageState = { ...this.pageState, page, total: Number(data.total || 0) }
      } catch (error) {
        if (!background && requestId === this.listRequestId && !this._isDestroyed)
          this.$message.error(failureMessage || error?.response?.data?.message || error?.message || '记录加载失败')
      } finally {
        if (requestId === this.listRequestId) this.loading = false
        this.scheduleRefresh()
      }
    },
    changeTag(tag) {
      this.activeTag = tag.label
      this.activeType = tag.type
      this.load(1)
    },
    changeLimit() {
      this.load(1)
    },
    statusClass(status) {
      return { 9: 'completed', 10: 'failed', 11: 'cancelled' }[Number(status)] || 'processing'
    },
    detailStatusName(status) {
      return statusNames[Number(status)] || '处理中'
    },
    detailTitle(title, status) {
      return Number(status) === 3 && (title === '' || title === null || title === undefined) ? '抖店标题同步中' : title
    },
    async openDetail(record) {
      this.detailRequestId++
      this.detail = record
      this.detailUnsupported = record.detail_supported ? '' : '该类型详情暂未接入'
      this.detailRows = []
      this.detailStatus = ''
      this.detailSkipped = ''
      this.detailSchema = record.detail_schema || ''
      this.detailPage = { page: 1, limit: 20, total: 0 }
      if (record.detail_supported) await this.loadDetail(1)
    },
    async openStockSkus(item) {
      this.stockSkuRequestId++
      this.stockSkuDetailId = Number(item.id)
      this.stockSkuRows = []
      this.stockSkuPage = { page: 1, limit: 20, total: 0 }
      this.stockSkuVisible = true
      await this.loadStockSkus(1)
    },
    closeStockSkus() { this.stockSkuRequestId++; this.stockSkuVisible = false; this.stockSkuRows = [] },
    async loadStockSkus(page = this.stockSkuPage.page) {
      if (!this.detail || !this.stockSkuDetailId) return
      const req = ++this.stockSkuRequestId, batchId = this.detail.id, detailId = this.stockSkuDetailId
      try {
        const data = this.responseData(await (this.detailSchema === 'sku_code' ? getDoudianSkuCodeDetailSkus : this.detailSchema === 'price' ? getDoudianPriceDetailSkus : getDoudianStockDetailSkus)(batchId, detailId, { page, limit: this.stockSkuPage.limit }))
        if (req !== this.stockSkuRequestId || !this.stockSkuVisible || this.detail?.id !== batchId || this.stockSkuDetailId !== detailId || this._isDestroyed) return
        this.stockSkuRows = Array.isArray(data.list) ? data.list : []
        this.stockSkuPage = { ...this.stockSkuPage, page, total: Number(data.total || 0) }
      } catch (error) { if (req === this.stockSkuRequestId && this.stockSkuVisible) this.$message.error(error?.response?.data?.message || error?.message || 'SKU结果加载失败') }
    },
    async loadDetail(page = this.detailPage.page, background = false) {
      if (!this.detail) return
      const batchId = this.detail.id
      const requestId = ++this.detailRequestId
      if (!background) this.detailLoading = true
      this.detailUnsupported = ''
      try {
        const params = { page, limit: this.detailPage.limit }
        if (this.detailStatus !== '') params.status = this.detailStatus
        if (this.detailSkipped !== '') params.is_skipped = this.detailSkipped
        const data = this.responseData(await getDoudianBatchDetail(batchId, params))
        if (requestId !== this.detailRequestId || this.detail?.id !== batchId || this._isDestroyed) return
        this.materialClock = Date.now()
        this.detailRows = (Array.isArray(data.list) ? data.list : []).map(row => ({ ...row, image_materials: (row.image_materials || []).map(material => ({ ...material, refreshing: false, nextRefreshAt: this.materialClock + Number(material.retry_after_seconds || 0) * 1000 })) }))
        this.detail = {
          ...this.detail,
          cancelled_count: data.cancelled_count,
          pending_confirmation_count: data.pending_confirmation_count
        }
        this.detailSchema = data.detail_schema || this.detail.detail_schema || ''
        this.detailPage = { ...this.detailPage, page, total: Number(data.total || 0) }
        if (this.detailSchema === 'seven_day_return') await this.loadSevenDayReturnPolicyNames(batchId, requestId)
      } catch (error) {
        if (!background && requestId === this.detailRequestId && this.detail?.id === batchId && !this._isDestroyed)
          this.detailUnsupported = error?.response?.data?.message || error?.message || '详情加载失败'
      } finally {
        if (requestId === this.detailRequestId) this.detailLoading = false
        this.scheduleRefresh()
      }
    },
    changeDetailStatus(status, skipped = '') {
      this.detailStatus = status
      this.detailSkipped = this.allowsSkip ? skipped : ''
      this.loadDetail(1)
    },
    closeDetail() {
      this.detailRequestId++
      this.detailLoading = false
      this.detail = null
      this.detailRows = []
      this.detailUnsupported = ''
      this.detailSchema = ''
      this.scheduleRefresh()
    }
  }
}
</script>

<style>
.bm-records-cancel-confirm {
  max-width: calc(100vw - 32px);
}
.main-image-material-state { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.main-image-material-state small { max-width: 160px; overflow-wrap: anywhere; }
</style>

<style scoped>
.bm-records .purchase-limit-value {
  white-space: pre-line;
  line-height: 24px;
  min-width: 180px;
}
.bm-records {
  padding: 14px 20px;
  font-family: 'Noto Sans SC', sans-serif;
}
.bm-records__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}
.bm-records__queue {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f5f7fa;
  color: #606266;
  font-size: 12px;
}
.bm-records__queue span {
  margin-left: 16px;
}
.bm-records__queue small {
  display: block;
  margin-top: 6px;
  color: #909399;
}
.bm-records__actions {
  min-width: 150px;
}
.bm-records__actions button {
  margin: 2px 4px 2px 0;
  white-space: nowrap;
}
.bm-records__status.cancelled {
  background: #f0f0f0;
  color: #909399;
}
.bm-records button,
.bm-records select {
  height: 26px;
  padding: 0 10px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  color: #666;
  cursor: pointer;
  font-size: 11px;
}
.bm-records button.active {
  border-color: #1677ff;
  background: #1677ff;
  color: #fff;
}
.bm-records button:disabled {
  color: #bbb;
  cursor: not-allowed;
}
.bm-records__table-wrap,
.bm-records__modal {
  overflow: hidden;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #fff;
}
.bm-records__table-wrap {
  overflow-x: auto;
}
.bm-records__modal {
  overflow-x: auto;
}
.bm-records table {
  width: 100%;
  min-width: 1060px;
  border-collapse: collapse;
}
.bm-records th {
  padding: 8px 12px;
  background: #f5f0e8;
  color: #666;
  font-size: 11px;
  text-align: left;
}
.bm-records td {
  padding: 12px;
  border-bottom: 1px solid #f5f5f5;
  font-size: 12px;
}
.bm-records td small {
  display: block;
  margin-top: 3px;
  color: #999;
  font-size: 11px;
  line-height: 1.5;
}
.bm-records__progress {
  display: inline-block;
  width: 90px;
  height: 6px;
  overflow: hidden;
  border-radius: 3px;
  background: #f0f0f0;
}
.bm-records__progress i {
  display: block;
  height: 100%;
  background: #e05858;
}
.bm-records td > b {
  margin-left: 6px;
  color: #c8443c;
  font-size: 10px;
}
.bm-records__status {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
}
.bm-records__status.completed {
  background: #f6ffed;
  color: #52c41a;
}
.bm-records__status.failed {
  background: #fff5f5;
  color: #c8443c;
}
.bm-records__status.processing {
  background: #fff7e6;
}
.bm-records__status.processing {
  color: #fa8c16;
}
.success {
  color: #52c41a;
}
.danger {
  color: #c8443c;
}
.blue {
  border-color: #91caff !important;
  background: #e6f4ff !important;
  color: #1677ff !important;
}
.bm-records footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #f5f0e8;
  color: #999;
  font-size: 11px;
}
.bm-records__modal footer {
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}
.bm-records__detail-pagination {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
  padding: 0;
}
.bm-records__detail-pagination ::v-deep .el-pagination__jump {
  margin-left: 0;
}
.bm-records__mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.4);
}
.bm-records__modal {
  display: flex;
  flex-direction: column;
  width: min(1000px, 100%);
  max-height: 85vh;
  overflow: hidden;
}
.bm-records__modal-body {
  min-height: 0;
  overflow: auto;
}
.bm-records__modal > header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid #e0e0ec;
}
.bm-records__modal nav {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 18px;
}
.bm-records__modal nav button {
  flex-shrink: 0;
  white-space: nowrap;
}
.bm-records__modal table {
  width: calc(100% - 36px);
  margin: 0 18px;
  min-width: 700px;
}
.bm-records__error-column {
  width: 180px;
  max-width: 180px;
}
.bm-records__error-text {
  display: block;
  width: 180px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.empty {
  padding: 28px !important;
  text-align: center;
  color: #999;
}
</style>
